import { useCallback, useEffect, useRef, useState } from "react";

/** Wait so a fast briefing does not flash dots. */
export const OPENING_THINKING_DELAY_MS = 200;

/**
 * Track overlapping opening fetches. `startWork()` returns a settle fn that
 * is safe to call twice (finally + effect cleanup).
 * @param {(busy: boolean) => void} onBusyChange
 */
export function createOpeningWorkSession(onBusyChange) {
  let count = 0;
  const sync = () => onBusyChange(count > 0);
  return {
    startWork() {
      count += 1;
      sync();
      let settled = false;
      return () => {
        if (settled) return;
        settled = true;
        count = Math.max(0, count - 1);
        sync();
      };
    },
  };
}

/**
 * Thinking dots while auth or the first chat message is still on its way.
 * @param {{ authLoading?: boolean, hasAssistantMessage?: boolean }} [opts]
 */
export function useOpeningThinking({
  authLoading = false,
  hasAssistantMessage = false,
} = {}) {
  const [openingBusy, setOpeningBusy] = useState(false);
  const [showOpeningThinking, setShowOpeningThinking] = useState(false);
  const sessionRef = useRef(null);
  if (!sessionRef.current) {
    sessionRef.current = createOpeningWorkSession(setOpeningBusy);
  }

  const startOpeningWork = useCallback(() => sessionRef.current.startWork(), []);

  const waitingForOpening = (authLoading || openingBusy) && !hasAssistantMessage;

  useEffect(() => {
    if (!waitingForOpening) {
      setShowOpeningThinking(false);
      return undefined;
    }
    const t = setTimeout(
      () => setShowOpeningThinking(true),
      OPENING_THINKING_DELAY_MS,
    );
    return () => clearTimeout(t);
  }, [waitingForOpening]);

  return { showOpeningThinking, startOpeningWork };
}
